import api from './api';

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  userId: string;
  clientId: string;
  timesheetId?: string;
  issueDate: string;
  dueDate: string;
  lineItems: InvoiceLineItem[];
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  notes?: string;
  sentDate?: string;
  sentToEmail?: string;
  emailSubject?: string;
  emailBody?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInvoiceData {
  clientId: string;
  issueDate: string;
  dueDate: string;
  lineItems: InvoiceLineItem[];
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
  status?: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  notes?: string;
  timesheetId?: string;
}

export interface UpdateInvoiceData extends Partial<CreateInvoiceData> {}

export interface InvoiceFilters {
  status?: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  clientId?: string;
}

export const invoicesApi = {
  getAll: async (filters?: InvoiceFilters): Promise<Invoice[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.clientId) params.append('clientId', filters.clientId);
    
    const queryString = params.toString();
    const url = `/invoices${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data.data;
  },

  getById: async (id: string): Promise<Invoice> => {
    const response = await api.get(`/invoices/${id}`);
    return response.data.data;
  },

  create: async (data: CreateInvoiceData): Promise<Invoice> => {
    const response = await api.post('/invoices', data);
    return response.data.data;
  },

  update: async (id: string, data: UpdateInvoiceData): Promise<Invoice> => {
    const response = await api.put(`/invoices/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/invoices/${id}`);
  },

  generateFromTimesheet: async (
    timesheetId: string,
    issueDate: string,
    dueDate: string
  ): Promise<Invoice> => {
    const response = await api.post(`/invoices/from-timesheet/${timesheetId}`, {
      issueDate,
      dueDate,
    });
    return response.data.data;
  },

  getReadyTimesheets: async (): Promise<Array<{
    clientId: string;
    clientName: string;
    invoiceSubmissionDate: number;
    timesheets: Array<{
      id: string;
      startDate: string;
      endDate: string;
      entries: any[];
    }>;
  }>> => {
    const response = await api.get('/invoice-generation/ready');
    return response.data.data;
  },

  generateInvoices: async (options?: {
    clientId?: string;
    autoApprove?: boolean;
  }): Promise<{
    generated: number;
    invoices: Invoice[];
    errors?: Array<{ clientId: string; clientName: string; error: string }>;
  }> => {
    const response = await api.post('/invoice-generation/generate', options || {});
    return response.data.data;
  },

  generateMonthlyInvoice: async (data: {
    timesheetIds: string[];
    clientId: string;
    issueDate: string;
    dueDate: string;
    autoApprove?: boolean;
  }): Promise<Invoice> => {
    const response = await api.post('/invoice-generation/generate-monthly', data);
    return response.data.data;
  },

  sendEmail: async (id: string, data: {
    to: string;
    subject?: string;
    body?: string;
  }): Promise<Invoice> => {
    const response = await api.post(`/invoices/${id}/send-email`, data);
    return response.data.data;
  },

  getPDF: async (id: string): Promise<Blob> => {
    const response = await api.get(`/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
};





